
import React, { useMemo } from "react";
import { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";

import DateInput from "./DateInput";
import { MultiViewCalendar } from "@progress/kendo-react-dateinputs";
import { Popup } from "@progress/kendo-react-popup";

import { useValidation } from "../utils/ValidationProvider";
import "./DateRangeInput.scss";

/**
 * DateRangeInput
 * @module
 * @param {string} name - Name
 * @param {func} onChange - Function invoked on value change
 * @param {object} [value] - Current input value
 * @param {bool} [disabled] - Is input readonly? => user can not change value
 * @param {bool} [required] - Is input required? => can not be left empty
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {Date | number} [min] - Minimum date
 * @param {Date | number} [max] - Maximum date
 * @param {number} [tabIndex] - Tabindex of datepicker => index of input using <tab> to navigate
 * @param {func} [onBlur] - Function invoked on input blur
 *
 *              Date                         Format : format je fixny v tvare "dd.mm.yyyy" !!!!
 */

const START = "START";
const END = "END";

const DateRangeInput = (props) => {
  const [changeState, setChangeState] = useState({
    start: false,
    end: false
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const anchor = useRef(null);
  const popupTimeoutRef = useRef(null);
  const startRef = useRef(null);
  const endRef = useRef(null);
  const calendarRef = useRef(null);
  const focusRef = useRef(null);

  const _value = useRef(props.value ?? { start: null, end: null });

  const field = useMemo(() => ({
    field: props.name,
    title: props.title,
    type: props.type,
    min: props.min,
    max: props.max,
    minLength: props.minLength,
    maxLength: props.maxLength,
    validationMessage: props.validationMessage,
    pattern: props.pattern,
    required: props.required,
    disabled: props.disabled,
    recommended: props.recommended,
    validationAllowed: props.validationAllowed,
    isInvalid: props.isInvalid
  }), [props.disabled, props.max, props.maxLength, props.min, props.minLength, props.name, props.pattern, props.required, props.title, props.type, props.validationMessage, props.recommended, props.validationAllowed, props.isInvalid]);

  const { validate, setValidationValue } = useValidation(field, _value.current);

  useEffect(() => {
    if (calendarOpen) calendarRef?.current.focus();
  }, [calendarOpen]);

  useEffect(() => {
    if (changeState.start && changeState.end) closePopup();
    // eslint-disable-next-line
  }, [changeState]);

  const getValue = () => {
    _value.current = {
      start: startRef.current?.state.date ?? null,
      end: endRef.current?.state.date ?? null
    };
    return _value.current;
  }

  const setStartDate = (date) => {
    if (!date) return;

    startRef.current.onChange({
      value: startRef.current.dateToString(date),
    });
  };

  const setEndDate = (date) => {
    if (!date) return;

    endRef.current.onChange({
      value: endRef.current.dateToString(date),
    });
  };

  const onChange = (from, name, value) => {
    setChangeState({
      start: from === START ? true : changeState.start,
      end: from === END ? true : changeState.end,
    });

    const newValue = {
      start: (from === START ? value : _value.current.start) ?? null,
      end: (from === END ? value : _value.current.end) ?? null,
    }
    _value.current = newValue;
    if (props.onChange) props.onChange(props.name, newValue);
  }

  const checkOrder = () => {
    const value = _value.current;
    if (!value.start || !value.end) return;
    if (value.start > value.end) {
      let oldStart = value.start;
      let oldEnd = value.end;

      setStartDate(oldEnd);
      setEndDate(oldStart);
    }
  };

  const onFocus = () => {
    if (calendarOpen) return setTimeout(cancelClosePopup(), 1);

    if (props.onFocus && !calendarOpen) props.onFocus();

    setCalendarOpen(true);
  };

  const getNewValue = () => {
    const startValue = startRef.current?.state.date || (startRef.current?.state.value === "__.__.____" ? null : startRef.current?.state.value === "" ? null : startRef.current?.state.value);
    const endValue = endRef.current?.state.date || (endRef.current?.state.value === "__.__.____" ? null : endRef.current?.state.value === "" ? null : endRef.current?.state.value);

    const newValue = {
      start: startValue,
      end: endValue
    }

    return newValue;
  };

  const onBlur = () => {
    setValidationValue(getNewValue());
    validate();
    closePopup();
  };

  const cancelClosePopup = () => {
    clearTimeout(popupTimeoutRef.current);
  };

  const closePopup = () => {
    clearTimeout(popupTimeoutRef.current);
    popupTimeoutRef.current = setTimeout(() => {
      setCalendarOpen(false);
      setChangeState({
        start: false,
        end: false
      });

      checkOrder();

      if (props.onBlur) props.onBlur();
      validate();
    }, 200);
  };

  const setNewDates = (data) => {
    let newValue = { ...data.value };
    if (!newValue) return;

    let newDateRange = {
      start: newValue.start,
      end: newValue.end
    }
    _value.current = newDateRange;

    if (newValue.start) setStartDate(newValue.start);
    if (newValue.end) setEndDate(newValue.end);

    setValidationValue(newDateRange);
    validate();
  };

  let value = props.value ?? getValue();
  const startValue = props.value?.start instanceof Date ? props.value?.start : props.value?.start ? new Date(props.value?.start) : null;
  const endValue = props.value?.end instanceof Date ? props.value?.end : props.value?.end ? new Date(props.value?.end) : null;
  return (
    <div className="k-dateinput-wrap wrap-range">
      <span
        className="dpop-range"
        ref={anchor}
        onFocus={onFocus}
        onBlur={onBlur}
        tabIndex={props.tabIndex}
      >
        <div className={`${props.noFormInput ? "" : "form-input"} range-input ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}>
          <p className={"range-text " + (props.disabled && !startValue ? "range-disabled" : "")}> od </p>
          <DateInput
            type="date"
            // placeholder="Od"
            className={"range-cust-input " + (props.disabled ? "range-cust-input-disabled" : "")}
            // validationMessage=" "
            name={props.name + "-start"}
            value={startValue}
            ref={startRef}
            nobuttons={true}
            onChange={(n, v) => onChange(START, n, v)}
            onClear={() => onChange(START)}
            min={props.min ?? undefined}
            max={props.max ?? undefined}
            disabled={props.disabled}
            required={props.required}
            noFormInput={props.noFormInput}
          />
          <div className="range-filler"></div>
        </div>
        <div className={`${props.noFormInput ? "" : "form-input"} range-input ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}>
          <p className={"range-text " + (props.disabled && !endValue ? "range-disabled" : "")}> do </p>
          <DateInput
            type="date"
            //  placeholder="Do"
            className={"range-cust-input " + (props.disabled ? "range-cust-input-disabled" : "")}
            // validationMessage=" "
            name={props.name + "-end"}
            value={endValue}
            ref={endRef}
            nobuttons={true}
            onChange={(n, v) => onChange(END, n, v)}
            onClear={() => onChange(END)}
            min={props.min ?? undefined}
            max={props.max ?? undefined}
            disabled={props.disabled}
            required={props.required}
            noFormInput={props.noFormInput}
          />
          <div className="range-filler"></div>
        </div>
      </span>

      <Popup
        anchor={anchor.current}
        show={calendarOpen}
        className={"date-input-popup-wrapper"}
      >
        <div ref={focusRef}>
          <MultiViewCalendar
            animate={false}
            mode="range"
            activeRangeEnd={value.start ? (value.end ? (changeState.start ? (changeState.end ? null : "end") : "start") : "end") : "start"}
            min={props.min ?? undefined}
            max={props.max ?? undefined}
            value={value}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={setNewDates}
            ref={calendarRef}
          />
        </div>
      </Popup>
    </div>
  );
}

DateRangeInput.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.object,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  validationMessage: PropTypes.string,
  onBlur: PropTypes.func,
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  tabIndex: PropTypes.number,
};

export default DateRangeInput;